import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "../../components/header/Header";
import { useAuth } from "../../context/auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Footer from "../../components/footer/Footer";
import { toast } from "react-hot-toast";
import FavoriteIcon from "@mui/icons-material/Favorite";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

const WishlistPage = () => {
  const [wishlist, setWishlist] = useState([]);
  const [productList, setProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [auth] = useAuth();
  const isLoggedIn = !!auth.token;
  const local = JSON.parse(localStorage.getItem("auth"));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoggedIn) {
      getWishlist();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (wishlist.length > 0) {
      getProductList();
    } else {
      setIsLoading(false);
    }
  }, [wishlist]);

  const apiUrl = `${process.env.REACT_APP_API}`;

  const getWishlist = async () => {
    try {
      setIsLoading(true);
      const token = local.token;
      const res = await axios.get(`${apiUrl}/wishlist`, {
        headers: {
          Authorization: token,
        },
      });
      setWishlist(res.data.products);
    } catch (err) {
      console.error(err.message);
      setIsLoading(false);
    }
  };

  const getProductList = async () => {
    try {
      const promises = wishlist.map(async (product) => {
        const res = await axios.get(`${apiUrl}/product/${product._id}`);
        return res.data;
      });
      const products = await Promise.all(promises);
      setProductList(products);
      setIsLoading(false);
    } catch (err) {
      console.error(err.message);
      setIsLoading(false);
    }
  };

  const removeProductFromWishlist = async (productId, productName) => {
    try {
      const auth = JSON.parse(localStorage.getItem("auth"));
      if (!auth) {
        return;
      }
      await axios.delete(`${apiUrl}/wishlist/delete/${productId}`);
      const updatedWishlist = wishlist.filter(
          (products) => products._id !== productId
      );
      setWishlist(updatedWishlist);
      toast.success(`"${productName}" removed from wishlist`);
    } catch (err) {
      console.error(err.message);
      toast.error("Error removing from wishlist");
    }
  };

  if (!isLoggedIn) {
    return (
        <>
          <Header />
          <div className="container my-5">
            <div className="row justify-content-center">
              <div className="col-md-6">
                <div className="card shadow-lg border-0 text-center p-5">
                  <FavoriteBorderIcon style={{ fontSize: "5rem", color: "#c70044", margin: "0 auto" }} />
                  <h3 className="mt-4 mb-3">My Wishlist</h3>
                  <p className="text-muted mb-4">
                    Please log in to view your saved items and wishlist.
                  </p>
                  <button
                      className="btn btn-lg px-5 wishlist-login-button"
                      onClick={() => navigate("/login", { state: { from: location.pathname } })}
                  >
                    Login to Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </>
    );
  }

  return (
      <>
        <Header />
        <div className="wishlist-page" style={{ backgroundColor: "#f8f9fa", minHeight: "80vh" }}>
          <div className="container py-5">
            {/* Header Section */}
            <div className="text-center mb-5">
              <div className="wishlist-icon-wrapper mb-3">
                <FavoriteIcon style={{ fontSize: "3.5rem", color: "white" }} />
              </div>
              <h1 style={{ color: "#002e6a", fontWeight: "700" }}>
                My Wishlist
              </h1>
              <p className="text-muted">
                {productList.length > 0
                    ? `You have ${productList.length} ${productList.length === 1 ? 'item' : 'items'} saved`
                    : 'Save your favorite items here'}
              </p>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="text-center my-5">
                  <div className="spinner-border text-primary" role="status" style={{ color: "#c70044" }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading your wishlist...</p>
                </div>
            )}

            {/* Empty Wishlist */}
            {!isLoading && productList.length === 0 && (
                <div className="row justify-content-center">
                  <div className="col-md-6">
                    <div className="card shadow-sm border-0 text-center p-5">
                      <FavoriteBorderIcon style={{ fontSize: "5rem", color: "#ccc", margin: "0 auto" }} />
                      <h4 className="mt-4 mb-3">Your wishlist is empty</h4>
                      <p className="text-muted mb-4">
                        Start adding items you love to your wishlist!
                      </p>
                      <button
                          className="btn btn-lg px-5 explore-button"
                          onClick={() => navigate("/")}
                      >
                        Start Shopping
                      </button>
                    </div>
                  </div>
                </div>
            )}

            {/* Wishlist Items */}
            {!isLoading && productList.length > 0 && (
                <div className="row g-4">
                  {productList.map((item) => (
                      <div key={item._id} className="col-md-6 col-lg-4 col-xl-3">
                        <div className="card wishlist-card h-100 border-0 shadow-sm">
                          <div className="position-relative">
                            {/* Favorite Badge */}
                            <div className="favorite-badge">
                              <FavoriteIcon style={{ color: "#c70044" }} />
                            </div>

                            {/* Product Image */}
                            <Link to={`/products/${item._id}`}>
                              <img
                                  src={item.images.front}
                                  alt={item.productName}
                                  className="card-img-top wishlist-image"
                                  style={{
                                    height: "300px",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                  }}
                                  loading="lazy"
                              />
                            </Link>
                          </div>

                          <div className="card-body d-flex flex-column">
                            {/* Product Name */}
                            <Link
                                to={`/products/${item._id}`}
                                className="text-decoration-none"
                            >
                              <h6 className="card-title mb-2" style={{ color: "#002e6a", fontWeight: "600" }}>
                                {item.productName}
                              </h6>
                            </Link>

                            {/* Price */}
                            <div className="mb-3">
                              {item.salePrice > 0 ? (
                                  <div>
                            <span className="price-sale" style={{ color: "#c70044", fontSize: "1.25rem", fontWeight: "700" }}>
                              ${item.salePrice}
                            </span>
                                    <span className="price-original ms-2" style={{ textDecoration: "line-through", color: "#999", fontSize: "1rem" }}>
                              ${item.productPrice}
                            </span>
                                  </div>
                              ) : (
                                  <span className="price" style={{ color: "#002e6a", fontSize: "1.25rem", fontWeight: "700" }}>
                            ${item.productPrice}
                          </span>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-auto">
                              <Link to={`/products/${item._id}`} className="btn btn-primary w-100 mb-2 view-button">
                                <ShoppingCartIcon fontSize="small" className="me-1" />
                                View Product
                              </Link>
                              <button
                                  className="btn btn-outline-danger w-100 remove-button"
                                  onClick={() => removeProductFromWishlist(item._id, item.productName)}
                              >
                                <DeleteIcon fontSize="small" className="me-1" />
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                  ))}
                </div>
            )}
          </div>
        </div>
        <Footer />

        <style jsx>{`
        .wishlist-icon-wrapper {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #002e6a 0%, #c70044 100%);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 25px rgba(199, 0, 68, 0.3);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .wishlist-card {
          border-radius: 15px;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .wishlist-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15) !important;
        }

        .wishlist-image {
          transition: transform 0.3s ease;
        }

        .wishlist-card:hover .wishlist-image {
          transform: scale(1.1);
        }

        .favorite-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          z-index: 10;
        }

        .view-button {
         
          border: #002e6a;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .view-button:hover {
          background: linear-gradient(135deg, #001a40 0%, #a00036 100%);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(199, 0, 68, 0.4);
        }

        .remove-button {
          border-color: #c70044;
          color: #c70044;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .remove-button:hover {
          background-color: #c70044;
          border-color: #c70044;
          color: white;
          transform: translateY(-2px);
        }

        .explore-button {
          background: linear-gradient(135deg, #002e6a 0%, #c70044 100%);
          border: none;
          color: white;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .explore-button:hover {
          background: linear-gradient(135deg, #001a40 0%, #a00036 100%);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(199, 0, 68, 0.4);
        }

        .wishlist-login-button {
          background: linear-gradient(135deg, #002e6a 0%, #c70044 100%);
          border: none;
          color: white;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .wishlist-login-button:hover {
          background: linear-gradient(135deg, #001a40 0%, #a00036 100%);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(199, 0, 68, 0.4);
          color: white;
        }

        .card-title {
          min-height: 40px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .wishlist-icon-wrapper {
            width: 80px;
            height: 80px;
          }
        }
      `}</style>
      </>
  );
};

export default WishlistPage;